/**
 * Format file size for display.
 */
interface FormatFileSizeOptions {
  mode?: 'trimmed' | 'fixed-one' | 'conference-card' | 'ladder';
  nullish?: string;
}

export function formatFileSize(
  bytes: number | undefined | null,
  options: FormatFileSizeOptions = {},
): string {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return options.nullish ?? 'Unknown';

  // Ladder modes keep .toFixed(1) for KB+ and integer bytes for <1KB, capping at GB.
  // 'conference-card' hides zero (''); 'ladder' renders '0 B'. 'ladder' restores the
  // pre-dedup per-file formatters (CallFileDetailsDialog / useCallFileNotifications /
  // FilesContainer) which kept the trailing .0 that the default 'trimmed' mode strips
  // (e.g. "2.0 MB", not "2 MB").
  if (options.mode === 'conference-card' || options.mode === 'ladder') {
    if (bytes === 0) return options.mode === 'conference-card' ? '' : '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  if (bytes === 0) return options.mode === 'fixed-one' ? '0.0 B' : '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  // Clamp so bytes >= 1 TB render as GB (e.g. "1024 GB") instead of "… undefined".
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  const size = bytes / Math.pow(k, i);

  if (options.mode === 'fixed-one') return `${size.toFixed(1)} ${sizes[i]}`;

  return `${parseFloat(size.toFixed(1))} ${sizes[i]}`;
}
